/**
 * In-person pairs (session student-tutor pairs).
 */

import { apiClient } from '@/services/api-client';
import type { SessionStudentTutorPair } from '@/types/inperson/pair.types';
import { toLocalDateString } from '@/utils/format-date-utils';

/** Base path for in-person session pair endpoints. */
const SESSIONS_BASE = '/companies/current/sessions';

/** `GET /companies/current/sessions/student-tutor-pairs` */
export const getSessionStudentTutorPairs = async (params?: {
  page?: number;
  pageSize?: number;
  types?: string[];
  sessionId?: string;
  tutorId?: string;
  studentId?: string;
  establishmentId?: string;
  subjectId?: string;
  dateFrom?: Date;
  dateTo?: Date;
  tutorSearch?: string;
  studentSearch?: string;
}): Promise<{ data: SessionStudentTutorPair[]; total: number; page: number; pageSize: number }> => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append('page', params.page.toString());
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());

  params?.types?.forEach((t) => searchParams.append('types', t));

  if (params?.sessionId) searchParams.append('sessionId', params.sessionId);
  if (params?.tutorId) searchParams.append('tutorId', params.tutorId);
  if (params?.studentId) searchParams.append('studentId', params.studentId);
  if (params?.establishmentId) searchParams.append('establishmentId', params.establishmentId);
  if (params?.subjectId) searchParams.append('subjectId', params.subjectId);
  if (params?.dateFrom) searchParams.append('dateFrom', toLocalDateString(params.dateFrom));
  if (params?.dateTo) searchParams.append('dateTo', toLocalDateString(params.dateTo));
  if (params?.tutorSearch) searchParams.append('tutorSearch', params.tutorSearch);
  if (params?.studentSearch) searchParams.append('studentSearch', params.studentSearch);

  const path = `${SESSIONS_BASE}/student-tutor-pairs`;
  const qs = searchParams.toString();
  const url = qs ? `${path}?${qs}` : path;
  const { data } = await apiClient.get<{
    data: SessionStudentTutorPair[];
    total: number;
    page: number;
    pageSize: number;
  }>(url);
  return data;
};

/** `GET /companies/current/sessions/student-tutor-pairs/:id` */
export const getSessionStudentTutorPair = async (id: string): Promise<SessionStudentTutorPair> => {
  const { data } = await apiClient.get<SessionStudentTutorPair>(
    `${SESSIONS_BASE}/student-tutor-pairs/${id}`,
  );
  return data;
};
